import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Coins, Calculator, ChevronDown, ChevronUp } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './CryptocurrencyInvestmentCalculatorPage/components/CalculationForm';
import ResultsOverview from './CryptocurrencyInvestmentCalculatorPage/components/ResultsOverview';
import InvestmentChart from './CryptocurrencyInvestmentCalculatorPage/components/InvestmentChart';
import QuickAdjustmentSidebar from './CryptocurrencyInvestmentCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './CryptocurrencyInvestmentCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './CryptocurrencyInvestmentCalculatorPage/components/TipsSidebar';
import InfoContent from './CryptocurrencyInvestmentCalculatorPage/components/InfoContent';
import MarketTrends from './CryptocurrencyInvestmentCalculatorPage/components/MarketTrends';
import RussianLegalInfo from './CryptocurrencyInvestmentCalculatorPage/components/RussianLegalInfo';

// Определение начального состояния формы
export const initialFormData = {
  initialInvestment: '100000',
  cryptocurrencyType: 'bitcoin',
  investmentCurrency: 'rub',
  investmentPeriod: '12', // месяцев
  expectedGrowthRate: '15', // % в месяц
  reinvestProfit: false,
  reinvestmentPercentage: '50', // % от прибыли для реинвестирования
  feePercentage: '1.5', // Комиссии (%)
  riskLevel: 'medium', // low, medium, high
  taxRate: '13', // Налоговая ставка в России
  additionalInvestments: false,
  additionalInvestmentAmount: '10000',
  additionalInvestmentFrequency: 'monthly', // monthly, quarterly, annually
  includeTradingStrategy: false,
  tradingStrategy: 'hodl', // hodl, dca, active
  hasTranchesInvestment: false,
  tranchesCount: '3',
  tranchesFrequency: 'monthly',
  scenarioAnalysis: false,
  pessimisticScenario: '-10',
  optimisticScenario: '30',
};

// Определение типов криптовалют
export const cryptocurrencyTypes = [
  { id: 'bitcoin', name: 'Bitcoin (BTC)', defaultMonthlyGrowth: 15, riskScore: 70 },
  { id: 'ethereum', name: 'Ethereum (ETH)', defaultMonthlyGrowth: 12, riskScore: 75 },
  { id: 'binancecoin', name: 'Binance Coin (BNB)', defaultMonthlyGrowth: 10, riskScore: 80 },
  { id: 'solana', name: 'Solana (SOL)', defaultMonthlyGrowth: 18, riskScore: 85 },
  { id: 'cardano', name: 'Cardano (ADA)', defaultMonthlyGrowth: 8, riskScore: 82 },
  { id: 'xrp', name: 'XRP', defaultMonthlyGrowth: 7, riskScore: 80 },
  { id: 'polkadot', name: 'Polkadot (DOT)', defaultMonthlyGrowth: 9, riskScore: 83 },
  { id: 'avalanche', name: 'Avalanche (AVAX)', defaultMonthlyGrowth: 16, riskScore: 87 },
  { id: 'dogecoin', name: 'Dogecoin (DOGE)', defaultMonthlyGrowth: 20, riskScore: 95 },
  { id: 'tron', name: 'TRON (TRX)', defaultMonthlyGrowth: 8, riskScore: 84 },
];

// Определение типов валют для инвестиций
export const investmentCurrencies = [
  { id: 'rub', name: 'Российский рубль (₽)', symbol: '₽' },
  { id: 'usd', name: 'Доллар США ($)', symbol: '$' },
  { id: 'eur', name: 'Евро (€)', symbol: '€' },
  { id: 'cny', name: 'Китайский юань (¥)', symbol: '¥' },
];

// Определение периодичности дополнительных инвестиций
export const investmentFrequencies = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
];

// Определение торговых стратегий
export const tradingStrategies = [
  { id: 'hodl', name: 'HODL (Купить и держать)', riskScore: 65, description: 'Долгосрочное удержание криптовалюты без активных операций' },
  { id: 'dca', name: 'Dollar Cost Averaging (DCA)', riskScore: 60, description: 'Регулярные инвестиции фиксированной суммы' },
  { id: 'active', name: 'Активный трейдинг', riskScore: 90, description: 'Регулярная купля-продажа на основе технического анализа' },
  { id: 'grid', name: 'Сетчатая торговля (Grid)', riskScore: 75, description: 'Размещение ордеров на покупку и продажу в определенном диапазоне цен' },
  { id: 'iceberg', name: 'Айсберг-стратегия', riskScore: 80, description: 'Разделение крупных ордеров на мелкие для минимизации влияния на рынок' },
];

/**
 * CryptocurrencyInvestmentCalculatorPage Component - Калькулятор инвестиций в криптовалюту с различными сценариями и стратегиями.
 */
const CryptocurrencyInvestmentCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('cryptocurrency-investment-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор криптовалютных инвестиций | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор инвестиций в криптовалюту: расчет потенциальной доходности, анализ рисков, налоговые аспекты в России, моделирование различных сценариев инвестирования и торговых стратегий.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [investmentData, setInvestmentData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedInvestments, setSavedInvestments] = useState([]);
  const [showExtraTips, setShowExtraTips] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных инвестиций из localStorage ---
  useEffect(() => {
    const savedInvestmentsData = localStorage.getItem('savedCryptoInvestments');
    if (savedInvestmentsData) {
      try {
        setSavedInvestments(JSON.parse(savedInvestmentsData));
      } catch (e) {
        console.error("Error loading saved investments:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findGrowthRate = (id) => {
        const found = cryptocurrencyTypes.find(t => t.id === id);
        return found ? found.defaultMonthlyGrowth.toString() : initialFormData.expectedGrowthRate;
      };

      switch (subToolId) {
        case 'bitcoin':
          newState.cryptocurrencyType = 'bitcoin';
          newState.expectedGrowthRate = findGrowthRate('bitcoin');
          break;
        case 'ethereum':
          newState.cryptocurrencyType = 'ethereum';
          newState.expectedGrowthRate = findGrowthRate('ethereum');
          break;
        case 'dca-strategy':
          newState.includeTradingStrategy = true;
          newState.tradingStrategy = 'dca';
          break;
        case 'long-term':
          newState.investmentPeriod = '36';
          newState.includeTradingStrategy = true;
          newState.tradingStrategy = 'hodl';
          break;
        case 'regular-investment':
          newState.additionalInvestments = true;
          newState.additionalInvestmentFrequency = 'monthly';
          break;
        case 'scenario-analysis':
          newState.scenarioAnalysis = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setInvestmentData([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение инвестиции в localStorage
  const saveInvestment = (name) => {
    if (!results) return;

    const newSavedInvestment = {
      id: Date.now().toString(),
      name: name || `Инвестиция ${formData.initialInvestment}${investmentCurrencies.find(c => c.id === formData.investmentCurrency)?.symbol || '₽'} в ${cryptocurrencyTypes.find(c => c.id === formData.cryptocurrencyType)?.name}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      cryptocurrencyType: formData.cryptocurrencyType
    };

    const updatedSavedInvestments = [...savedInvestments, newSavedInvestment];
    setSavedInvestments(updatedSavedInvestments);
    localStorage.setItem('savedCryptoInvestments', JSON.stringify(updatedSavedInvestments));

    return newSavedInvestment;
  };

  // Удаление сохраненной инвестиции
  const deleteSavedInvestment = (id) => {
    const updatedSavedInvestments = savedInvestments.filter(investment => investment.id !== id);
    setSavedInvestments(updatedSavedInvestments);
    localStorage.setItem('savedCryptoInvestments', JSON.stringify(updatedSavedInvestments));
  };

  // Загрузка сохраненной инвестиции
  const loadSavedInvestment = (id) => {
    const investmentToLoad = savedInvestments.find(investment => investment.id === id);
    if (investmentToLoad) {
      setFormData(investmentToLoad.formData);
      setResults(investmentToLoad.results);
      if (investmentToLoad.investmentData) {
        setInvestmentData(investmentToLoad.investmentData);
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Coins className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор криптовалютных инвестиций'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              investmentData={investmentData}
              setInvestmentData={setInvestmentData}
              cryptocurrencyTypes={cryptocurrencyTypes}
              investmentCurrencies={investmentCurrencies}
              investmentFrequencies={investmentFrequencies}
              tradingStrategies={tradingStrategies}
              saveInvestment={saveInvestment}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <InvestmentChart 
                  investmentData={investmentData} 
                  results={results}
                  formData={formData}
                  cryptocurrencyTypes={cryptocurrencyTypes}
                  investmentCurrencies={investmentCurrencies}
                />
              </>
            )}

            {/* --- Текущие тренды российского крипторынка --- */}
            <MarketTrends />

            {/* --- Российские правовые аспекты --- */}
            <RussianLegalInfo />

            {/* --- Дополнительные советы (раскрывающиеся) --- */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
              <button 
                onClick={() => setShowExtraTips(!showExtraTips)}
                className="w-full flex justify-between items-center"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Coins className="w-6 h-6 text-indigo-500" />
                  Дополнительные советы по инвестированию в криптовалюту
                </h3>
                {showExtraTips ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {showExtraTips && (
                <div className="mt-4 prose prose-indigo dark:prose-invert max-w-none">
                  <h4>Безопасность инвестиций</h4>
                  <ul>
                    <li>Используйте надежные криптовалютные биржи, имеющие лицензии и хорошую репутацию</li>
                    <li>Храните значительные суммы на аппаратных кошельках (hardware wallets)</li>
                    <li>Используйте двухфакторную аутентификацию для защиты своих аккаунтов</li>
                    <li>Не храните все активы на одной платформе — диверсифицируйте хранение</li>
                    <li>Регулярно создавайте резервные копии ваших кошельков</li>
                  </ul>

                  <h4>Налогообложение в России</h4>
                  <ul>
                    <li>Ведите учет всех транзакций для корректного расчета налогов</li>
                    <li>Консультируйтесь с налоговым специалистом, знакомым с криптовалютным регулированием</li>
                    <li>Следите за изменениями в российском законодательстве относительно криптовалют</li>
                  </ul>

                  <h4>Управление рисками</h4>
                  <ul>
                    <li>Инвестируйте только те средства, потерю которых вы можете себе позволить</li>
                    <li>Используйте стоп-лоссы при активной торговле</li>
                    <li>Реализуйте часть прибыли при значительном росте рынка</li>
                    <li>Рассматривайте техническую надежность проектов перед инвестированием</li>
                  </ul>
                </div>
              )}
            </div>

            {/* --- Информационный контент о криптовалютных инвестициях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              cryptocurrencyTypes={cryptocurrencyTypes}
            />

            {/* --- Панель сравнения инвестиций --- */}
            <ComparisonSidebar 
              savedInvestments={savedInvestments}
              deleteSavedInvestment={deleteSavedInvestment}
              loadSavedInvestment={loadSavedInvestment}
              cryptocurrencyTypes={cryptocurrencyTypes}
              investmentCurrencies={investmentCurrencies}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .crypto-chart {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .crypto-chart {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .crypto-chart::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .crypto-chart::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .crypto-chart::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .crypto-chart::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .crypto-chart::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .crypto-chart::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .crypto-chart::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }

        /* Risk Meter Styling */
        .risk-meter {
          height: 8px;
          border-radius: 4px;
          background: linear-gradient(90deg, #4ade80 0%, #facc15 50%, #f87171 100%);
        }
        .risk-indicator {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid white;
          background-color: theme('colors.indigo.600');
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          position: absolute;
          top: -4px;
          transform: translateX(-50%);
          transition: left 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default CryptocurrencyInvestmentCalculatorPage;